/**
 * Display metadata for at-bat results.
 *
 * The API stores result codes; everything a human reads lives here so web and the
 * picker cannot drift. `AT_BAT_RESULTS` (in types/at-bats.ts) is the source of
 * truth for which codes exist — this file only labels them.
 */

import { AT_BAT_RESULTS, type AtBatResult } from '../types/at-bats';

export interface AtBatResultDisplay {
  /** Full label, used in the picker. */
  label: string;
  /** Scorebook shorthand, used in the log rows where space is tight. */
  abbreviation: string;
}

/**
 * Grouped the way a coach reads a box score, which is also the order the picker
 * renders in — hits first, then free passes, then outs.
 */
export const AT_BAT_RESULT_GROUPS: {
  label: string;
  results: AtBatResult[];
}[] = [
  {
    label: 'Hits',
    results: ['single', 'double', 'triple', 'home_run'],
  },
  {
    label: 'On base',
    results: ['walk', 'intentional_walk', 'hit_by_pitch', 'fielders_choice', 'reached_on_error'],
  },
  {
    label: 'Outs',
    results: [
      'strikeout_swinging',
      'strikeout_looking',
      'groundout',
      'flyout',
      'lineout',
      'popout',
      'sacrifice_bunt',
      'sacrifice_fly',
    ],
  },
];

export const AT_BAT_RESULT_DISPLAY: Record<AtBatResult, AtBatResultDisplay> = {
  single: { label: 'Single', abbreviation: '1B' },
  double: { label: 'Double', abbreviation: '2B' },
  triple: { label: 'Triple', abbreviation: '3B' },
  home_run: { label: 'Home run', abbreviation: 'HR' },
  walk: { label: 'Walk', abbreviation: 'BB' },
  intentional_walk: { label: 'Intentional walk', abbreviation: 'IBB' },
  hit_by_pitch: { label: 'Hit by pitch', abbreviation: 'HBP' },
  strikeout_swinging: { label: 'Strikeout (swinging)', abbreviation: 'K' },
  strikeout_looking: { label: 'Strikeout (looking)', abbreviation: 'ꓘ' },
  groundout: { label: 'Groundout', abbreviation: 'GO' },
  flyout: { label: 'Flyout', abbreviation: 'FO' },
  lineout: { label: 'Lineout', abbreviation: 'LO' },
  popout: { label: 'Popout', abbreviation: 'PO' },
  fielders_choice: { label: "Fielder's choice", abbreviation: 'FC' },
  reached_on_error: { label: 'Reached on error', abbreviation: 'ROE' },
  sacrifice_bunt: { label: 'Sacrifice bunt', abbreviation: 'SAC' },
  sacrifice_fly: { label: 'Sacrifice fly', abbreviation: 'SF' },
};

/** Results that put the batter on base — used to tint the row. */
const ON_BASE: ReadonlySet<AtBatResult> = new Set<AtBatResult>([
  'single',
  'double',
  'triple',
  'home_run',
  'walk',
  'intentional_walk',
  'hit_by_pitch',
  'fielders_choice',
  'reached_on_error',
]);

export const isOnBaseResult = (result: AtBatResult): boolean => ON_BASE.has(result);

export const atBatResultLabel = (result: AtBatResult): string =>
  AT_BAT_RESULT_DISPLAY[result]?.label ?? result;

export const atBatResultAbbreviation = (result: AtBatResult): string =>
  AT_BAT_RESULT_DISPLAY[result]?.abbreviation ?? result;

/**
 * Guards the case where a code is added to the API and the type but nobody adds a
 * label. Exercised by the unit tests rather than at runtime.
 */
export const allResultsHaveDisplay = (): boolean =>
  AT_BAT_RESULTS.every((result) => Boolean(AT_BAT_RESULT_DISPLAY[result]));
